import * as React from 'react';
import { Box, Container, Divider, Flex } from '@awning/components';
import Head from 'next/head';
import { Navigation } from '@/src/Navigation';
import { dasherize, getSomeData, undasherize } from '../../seoUtils';
import { LONG_STATES_TO_SHORT, STATE_NAMES } from '../../STATES';
import airbnbDataJSON from '../../AIRBNB_DATA.json' assert { type: 'json' };

const allAirbnbShortStates = Object.keys(airbnbDataJSON);
const allAirbnbStates = allAirbnbShortStates
  .sort()
  .map(s => STATE_NAMES[s as keyof typeof STATE_NAMES]);

export async function getStaticPaths() {
  const { stateLinks } = getSomeData();

  return {
    paths: ['/sitemap', ...stateLinks],
    fallback: 'blocking',
  };
}

const Link = (props: any) => {
  return (
    <Box>
      <Box as="a" {...props} href={`/a${props.href}`} />
    </Box>
  );
};

export async function getStaticProps(props: any) {
  const { params } = props;
  const isAirbnb = true;
  const isTopMarkets = params.page?.[0].includes('top-airbnb-markets') ?? false;

  let state = '',
    county = '',
    city = '',
    zipCode = '';

  if (isTopMarkets) {
    // skip the first part
    [, state = ''] = params.page ?? [];
  } else if (isAirbnb) {
    // if path starts with airbnb-market-data
    // skip the first part
    [, state = '', county = '', city = '', zipCode = ''] = params?.page ?? [];
  } else {
    [state = '', county = '', city = '', zipCode = ''] = params.page ?? [];
  }

  let { states, cities } = getSomeData();

  const allStates = states.sort().map(s => STATE_NAMES[s.state]);
  const allCities = cities.sort();

  //@ts-expect-error
  const shortState = state ? LONG_STATES_TO_SHORT[undasherize(state)?.toLowerCase()] : '';

  return {
    props: {
      state,
      shortState,
      county,
      city,
      zipCode,
      allStates,
      allCities,

      isAirbnbMarketData: isAirbnb,
      isTopMarkets: isTopMarkets,
    },
    revalidate: 86400,
  };
}

const Section = ({ title, listings }: any) => {
  return (
    <Box sx={{ my: 5 }}>
      <Box sx={{ text: 'xl', fontWeight: 'bold' }}>{title}</Box>
      <Box
        sx={{
          my: 5,
          display: 'grid',
          gridAutoFlow: 'row',
          gridTemplateColumns: {
            base: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          whiteSpace: 'nowrap',
          gap: 1,
          columnGap: 5,
          text: 'sm',
        }}
      >
        {listings}
      </Box>
      <Divider />
    </Box>
  );
};

const Breadcrumb = ({ isAirbnbMarketData, state }: any) => {
  return (
    <Flex>
      {state && (
        <Link
          sx={{ textDecoration: 'underline', color: 'gray.600', fontWeight: '400' }}
          href={`/sitemap`}
        >
          Sitemap
        </Link>
      )}

      {state && (
        <>
          &nbsp;/&nbsp;
          <Link
            sx={{ textDecoration: 'underline', color: 'gray.600', fontWeight: '400' }}
            href={`/sitemap/${isAirbnbMarketData ? 'airbnb-market-data/' : ''}${state}`}
          >
            {undasherize(state)}
          </Link>
          {isAirbnbMarketData && <Flex>&nbsp;(Airbnb Market Data)</Flex>}
        </>
      )}
    </Flex>
  );
};

export default function SiteMap(props: any) {
  const { isAirbnbMarketData, state, county, city, zipCode } = props;

  const renderCorrectPage = () => {
    if (props.isAirbnbMarketData) {
      if (props.state) {
        return <AirbnbStateOnlyPage {...props} />;
      }

      return <AllStatesPage {...props} />;
    }
  };

  const paths = [state, county, city, zipCode].filter(Boolean);
  const title =
    paths.length > 0 ? `Sitemap of homes in ${paths.join(', ')} | Awning` : 'Sitemap | Awning';

  // const description = `Awning has homes for sale${
  //   props.zipCode || props.city || props.county || props.state
  //     ? ' in ' + props.zipCode || props.city || props.county || props.state
  //     : ''
  // }. View photos and details. Browse by county, city, and zip. Filter by price, size, and more.`;

  return (
    <main>
      <Head>
        <title>{title}</title>
        {/* <meta property="description" content={description} /> */}
        <meta property="og:title" content={title} />
        <meta name="twitter:title" content={title} />
        <meta property="og:type" content="website" />
        {/* <meta property="og:description" content={description} /> */}
      </Head>
      <Navigation centerContainerContent={true} />
      <Container
        sx={{
          marginTop: { base: 4, lg: 8 },
          padding: { base: 4, lg: 0 },
        }}
      >
        <Box sx={{ text: 'sm' }}>
          <Breadcrumb
            state={state}
            county={county}
            city={city}
            zipCode={zipCode}
            isAirbnbMarketData={isAirbnbMarketData}
          />
        </Box>
        {renderCorrectPage()}
      </Container>
    </main>
  );
}

const AllStatesPage = () => {
  return (
    <>
      <Box sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ text: '2xl', fontWeight: 'bold' }}>Sitemap</Box>
      </Box>

      {allAirbnbStates?.length > 0 && (
        <Section
          title="Browse Airbnb Market Data by State"
          listings={allAirbnbStates.map((longName: string) => {
            return (
              <Link key={longName} href={`/sitemap/airbnb-market-data/${dasherize(longName)}`}>
                Airbnb Market Data in {longName}
              </Link>
            );
          })}
        />
      )}

      {allAirbnbStates?.length > 0 && (
        <Section
          title="Browse Best Places to Airbnb by State"
          listings={allAirbnbShortStates.map((shortState: string) => {
            return (
              <Link key={shortState} href={`/top-airbnb-markets/${shortState}`}>
                Best Places to Airbnb in {STATE_NAMES[shortState as keyof typeof STATE_NAMES]}
              </Link>
            );
          })}
        />
      )}
    </>
  );
};

const AirbnbStateOnlyPage = (props: { shortState: string; state: string }) => {
  const { shortState, state } = props;
  const stateData = airbnbDataJSON[shortState as keyof typeof airbnbDataJSON];
  const cities = (stateData.city as string[]).sort();
  const zipCodes = (stateData.zip_code as string[]).sort();
  const longStateName = undasherize(state);
  const tabs = [0, 1, 2, 3, 4];

  const getPartial = (tab: number) => {
    let partial = '';
    switch (tab.toString()) {
      case '0': {
        partial = `Airbnb Market Data`;
        break;
      }
      case '1': {
        partial = `Top Airbnbs`;
        break;
      }
      case '2': {
        partial = `Airbnb Average Daily Rate`;
        break;
      }
      case '3': {
        partial = `Airbnb Occupancy Rate`;
        break;
      }
      case '4': {
        partial = `Airbnb Revenue`;
        break;
      }
    }

    return partial;
  };

  const getPathname = (tab: number) => {
    let pathname = '';
    switch (tab.toString()) {
      case '1': {
        pathname = 'best-airbnb';
        break;
      }
      case '2': {
        pathname = 'rates';
        break;
      }
      case '3': {
        pathname = 'occupancy';
        break;
      }
      case '4': {
        pathname = 'revenue';
        break;
      }
      default: {
        pathname = '';
        break;
      }
    }

    return pathname;
  };

  return (
    <>
      <Box sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ text: '2xl', fontWeight: 'bold' }}>
          Sitemap of Airbnb Market Data in {longStateName}
        </Box>
      </Box>

      {cities?.length > 0 &&
        tabs.map(tab => {
          const partial = getPartial(tab);
          const pathname = getPathname(tab);

          return (
            <Section
              key={tab}
              title={`Browse ${partial} by city`}
              listings={cities.map((city: string) => {
                const href = `/airbnb-market-data/${dasherize(city)}-${shortState}/${pathname}`;
                return (
                  <Link key={`${city}-${pathname}`} href={href}>
                    {partial} in {city}, {longStateName}
                  </Link>
                );
              })}
            />
          );
        })}

      {zipCodes?.length > 0 &&
        tabs.map(tab => {
          const partial = getPartial(tab);
          const pathname = getPathname(tab);

          return (
            <Section
              key={tab}
              title={`Browse ${partial} by zip code`}
              listings={zipCodes.map((zipCode: string) => {
                const href = `/airbnb-market-data/${zipCode}-${shortState}/${pathname}`;
                return (
                  <Link key={`${zipCode}-${pathname}`} href={href}>
                    {partial} in {zipCode}, {longStateName}
                  </Link>
                );
              })}
            />
          );
        })}
    </>
  );
};
